/**
 * Copy for the in-app Help & User Guide — the same content as the standalone
 * "Entiovi Field Guide" artifact, restructured as data so `HelpPage.jsx` can
 * render it with this app's own components (Card, Badge, SearchInput)
 * instead of a second, parallel design system living inside the product.
 *
 * `roles` on a section is the same list `data-roles` carried in the artifact
 * — which roles the section is relevant to, used to dim/explain irrelevant
 * sections when someone picks a role lens that isn't their own.
 */

export const ROLES = {
  rep: { label: 'Sales Rep', tone: 'teal', mission: 'Own leads, accounts, deals and activities.', landing: 'Signing in lands you on the Rep dashboard — your open leads, today’s follow-ups, and your personal pipeline.' },
  manager: { label: 'Sales Manager', tone: 'sky', mission: 'Team pipeline, assignment, approvals and coaching.', landing: 'Signing in lands you on the Manager dashboard — your team’s pipeline, attainment, and who needs coaching.' },
  exec: { label: 'Executive', tone: 'violet', mission: 'Read-only visibility across the whole organisation.', landing: 'Signing in lands you on the Executive dashboard — company-wide revenue, pipeline and forecast, nothing to edit.' },
  admin: { label: 'Super Admin', tone: 'rose', mission: 'Full configuration and data access, including audit and security.', landing: 'Signing in lands you on the Admin dashboard — system health plus every configuration screen.' },
  partner: { label: 'Partner', tone: 'amber', mission: 'Submit leads on behalf of your partner company. Nothing else in the CRM is visible to you.', landing: 'Signing in lands you on Leads, showing only the leads you’ve submitted yourself.' },
};

const ALL = ['rep', 'manager', 'exec', 'admin'];

export const GROUPS = [
  {
    group: 'Start here',
    sections: [
      {
        id: 'signing-in',
        title: 'Signing in',
        roles: [...ALL, 'partner'],
        blurb: 'The sign-in screen offers Google, Microsoft or Enterprise SSO (SAML/OIDC), or a direct email and password. Whichever role your account holds decides what you see next — the same login form, four different worlds behind it.',
        howtos: [
          {
            q: 'Sign in for the first time',
            steps: [
              'Go to the sign-in page and enter the email and password your administrator gave you.',
              'If your organisation requires it, enter the MFA code from your authenticator app.',
              'You’ll land on your role’s dashboard automatically.',
              'Tick Remember me on a device you trust to skip this next time.',
            ],
          },
          {
            q: 'Forgot your password',
            steps: [
              'Click Forgot? next to the password field.',
              'Enter the email on your account — a reset link is sent if it matches one.',
              'The link expires after a short window; request a new one if it’s gone stale.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'Every role has its own landing page — you never have to navigate there yourself after signing in.' }],
      },
    ],
  },
  {
    group: 'Workspace',
    intro: 'Where you land every morning — what needs you today, not the full archive.',
    sections: [
      {
        id: 'dashboard',
        title: 'Dashboard',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'org', admin: 'full' },
        blurb: 'A different dashboard for every role, because "what matters today" isn’t the same question for a rep and for the CEO. Reps see their own open leads, overdue follow-ups and quota progress. Managers see the whole team’s pipeline. Executives and admins see company-wide KPIs.',
        howtos: [
          {
            q: 'Read your dashboard at a glance',
            steps: [
              'Reps: check "Due today" and "Overdue" first — these are follow-ups that will slip if ignored.',
              'Managers: the team roster shows attainment per rep, worst-first, so coaching starts with whoever needs it most.',
              'Executives / Admins: revenue, win-rate and pipeline tiles roll up the entire organisation.',
            ],
          },
        ],
        notes: [{ type: 'example', text: 'The Executive and Admin dashboards currently show an example-data badge — the live company-wide aggregates aren’t wired up yet. Rep and Manager dashboards are real, live data.' }],
      },
      {
        id: 'feed',
        title: 'Activity Feed',
        roles: ALL,
        blurb: 'A recent-first stream of everything logged against your records — calls, emails, meetings, notes. It’s the same list the Activities screen shows, just framed as "what just happened."',
        howtos: [
          {
            q: 'Catch up on what changed',
            steps: [
              'Open Activity Feed from the sidebar.',
              'Newest activity is always at the top — scroll down for history.',
              'Click any entry to jump to the lead, account or opportunity it belongs to.',
            ],
          },
        ],
      },
      {
        id: 'teams',
        title: 'My Team',
        badgeNew: true,
        roles: ['manager', 'admin'],
        scopes: { rep: null, manager: 'create, edit', exec: null, admin: 'full' },
        blurb: 'A sales manager’s own self-service team builder. Create your team, become its lead automatically, and add the reps who report to you — no waiting on an admin to set it up.',
        howtos: [
          {
            q: 'Create your team',
            steps: [
              'Open My Team and click Create team.',
              'Give it a short code (e.g. NORDICS) and a name.',
              'Optionally pick the reps to add straight away — search by name in the picker.',
              'You’re set as the team’s lead automatically. If you already lead a team, the new one nests under it as a sub-team; otherwise it’s your first, top-level team.',
            ],
          },
          {
            q: 'Manage the roster afterward',
            steps: [
              'Open the team from My Team’s list.',
              'Click Add members to bring in more reps, or the ✕ next to a name to remove them.',
              'Removing someone keeps their history — they’re marked as having left, not erased from the record.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'Reps and executives don’t see My Team at all — team creation and rostering is a manager/admin capability.' }],
      },
    ],
  },
  {
    group: 'Sell',
    intro: 'The day-to-day: leads coming in, deals moving forward, quotes going out.',
    sections: [
      {
        id: 'leads',
        title: 'Leads',
        roles: ['rep', 'manager', 'admin', 'partner'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full', partner: 'own (submitted)' },
        blurb: 'Every unqualified prospect starts here. Score, status and source tell you where to spend your next ten minutes; converting a lead is what turns it into an account, a contact, and — if it’s ready — an opportunity.',
        howtos: [
          {
            q: 'Work your lead list',
            steps: [
              'Open Leads — the default view is All Open Leads, marked with a small pin so you always know you’re on the standard view.',
              'Sort by Score to find who’s most sales-ready right now.',
              'Click a lead to open its record, log a call or email, and update its status.',
            ],
          },
          {
            q: 'Change owner or transfer to the queue',
            badgeNew: true,
            steps: [
              'Select one or more leads, then choose Change owner from the bulk actions or a lead’s own menu.',
              'Pick the new owner and confirm — this only appears if you hold assignment rights (managers and admins).',
              'To hand an unworked lead back to the pool instead of a person, choose Transfer to queue. Only leads still in New status can go back to the queue; anything already worked is refused with a clear reason.',
            ],
          },
          {
            q: 'Import leads from a CSV file',
            badgeNew: true,
            steps: [
              'Click Import above the lead list (managers and admins only — reps don’t hold this permission).',
              'Not sure how to format your file? Download the sample CSV from inside the import dialog first.',
              'Upload the file — columns are matched to lead fields automatically by header name; only Last name and Company are required.',
              'Review the preview: how many rows are valid, how many look like duplicates, and choose what to do with a match — skip it, update it, fill in only the blanks, or create a new lead anyway.',
              'Start the import and watch it run — a progress bar shows rows processed as it goes, and a downloadable error report covers anything that failed.',
            ],
          },
          {
            q: 'Say whether a lead wants a product or a new project',
            badgeNew: true,
            steps: [
              'On the lead form, use the Interest toggle to mark it Product or Project.',
              'Product opens a picker from the catalogue; Project is a free-text name for something that isn’t catalogued yet.',
              'Leave it unset if you genuinely don’t know yet — it’s optional.',
            ],
          },
          {
            q: 'Submit a lead as a partner',
            badgeNew: true,
            steps: [
              'Click New Lead and fill in the contact and company details — the same form everyone else uses.',
              'It’s automatically credited to your partner company and stays owned by you until your contact at the organisation picks it up.',
              'You’ll only ever see leads you personally submitted here — nothing else in the CRM is visible to a partner login.',
            ],
          },
        ],
        notes: [
          { type: 'tip', text: 'Trying to change owner with nothing selected shows a reminder to select at least one lead first, rather than doing nothing silently.' },
          { type: 'tip', text: 'Every lead list has a Partner column and filter, showing which partner company referred it, if any — helpful for spotting where your best leads are actually coming from.' },
        ],
      },
      {
        id: 'accounts',
        title: 'Accounts & Contacts',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'view', admin: 'full' },
        blurb: 'Accounts are the companies you sell to; Contacts are the people inside them. An account’s Overview tab shows company details and lifecycle stage side by side; its Hierarchy tab shows parent/subsidiary relationships.',
        howtos: [
          {
            q: 'Open an account and see the full picture',
            steps: [
              'Search or filter the Accounts list, then click through to a record.',
              'The Overview tab lays out company info and lifecycle stage, three columns per row.',
              'Related contacts, opportunities and activities each get their own tab on the same record.',
            ],
          },
          {
            q: 'Never miss a contact’s anniversary',
            steps: [
              'Open Contacts and click the Anniversaries button.',
              'See who has a work anniversary in the next 7 days.',
              'Send a quick note using the Customer Work Anniversary email template — no pitch, just goodwill.',
            ],
          },
        ],
      },
      {
        id: 'opportunities',
        title: 'Opportunities & Pipeline',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'view', admin: 'full' },
        blurb: 'A deal in progress, tracked stage by stage. The same records render two ways: a sortable list, or a kanban board — pick whichever suits the moment.',
        howtos: [
          {
            q: 'Move a deal forward',
            steps: [
              'Open Opportunities, then toggle to the board view for a kanban layout by stage.',
              'Drag a card to its new stage, or open the record and change stage from there — the full stage history is kept either way.',
              'Closing as Lost requires a reason, so the team learns something from every deal that doesn’t land.',
            ],
          },
        ],
      },
      {
        id: 'quotations',
        title: 'Quotations & Approvals',
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team, approve', exec: null, admin: 'full' },
        blurb: 'A formal price quote for a prospect, with line items, discounts, and a revision history when terms change. Above a manager’s discretionary discount threshold, a quote needs sign-off before it can be sent.',
        howtos: [
          {
            q: 'Send a quote for approval',
            steps: [
              'Build the quotation against an opportunity, adding line items from the product catalogue.',
              'Click Submit for approval if the discount needs sign-off — otherwise send it directly.',
              'Track its status: draft → pending approval → approved/rejected → sent.',
            ],
          },
          {
            q: 'Approve or reject a pending quote (managers)',
            steps: [
              'Open the Quote approvals tab under Quotations.',
              'Review the discount and margin against the deal.',
              'Approve, or reject with a reason the rep can act on.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'A revised quotation is a new row sharing the same root — the original terms stay on file even after a revision supersedes them.' }],
      },
    ],
  },
  {
    group: 'Engage',
    intro: 'Everything that keeps a prospect warm between meetings.',
    sections: [
      {
        id: 'activities',
        title: 'Activities & Follow-ups',
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full' },
        blurb: 'Calls, meetings, emails, and tasks — logged against a lead, contact, account or opportunity. The same screen switches between a list, a calendar month view, and a dedicated follow-ups queue.',
        howtos: [
          {
            q: 'Log a call or meeting',
            steps: [
              'From a record’s page, click Log activity and choose the type.',
              'Fill in the outcome and, if there’s a next step, set a follow-up date.',
              'Mark it complete when it’s done — overdue items surface on your dashboard until you do.',
            ],
          },
          {
            q: 'Switch views',
            steps: [
              'Use the view toggle at the top of Activities to switch between List, Calendar, and Follow-ups.',
              'Calendar view is a month grid of the same rows — good for spotting a light week early.',
            ],
          },
        ],
      },
      {
        id: 'templates',
        title: 'Email Templates',
        badgeNew: true,
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full' },
        blurb: 'Reusable email copy with live merge fields — {{lead.first_name}}, {{contact.company}}, {{anniversary.years_ordinal}} and more — so nobody’s typing "Dear [NAME]" by hand. Five shared starting points already cover common moments: cold outreach, meeting follow-up, proposal sent, work-anniversary goodwill, and re-engaging a cold lead.',
        howtos: [
          {
            q: 'Send a templated email',
            steps: [
              'From a lead or contact’s record, click Email and pick a template from the folder list.',
              'Preview shows the merge fields resolved against that specific record before you send.',
              'Send — it goes out through the organisation’s connected provider and logs itself as an activity automatically.',
            ],
          },
          {
            q: 'Create your own template',
            steps: [
              'Open Email Templates and click New template.',
              'Write the subject and body, dropping in merge tokens from the picker — Lead, Contact, Anniversary, Sender or Organisation fields.',
              'Mark it Shared so the rest of the team can use it too, or keep it personal.',
            ],
          },
        ],
      },
      {
        id: 'email',
        title: 'Email',
        badgeNew: true,
        roles: ['rep', 'manager', 'admin'],
        scopes: { rep: 'own', manager: 'team', exec: null, admin: 'full' },
        blurb: 'A standalone inbox for everything you’ve sent through the CRM — compose to any lead, contact or account without leaving the page you’re on, then track whether it actually got read. A small marker on each row shows whether it was opened, and how many times.',
        howtos: [
          {
            q: 'Compose a one-off email',
            steps: [
              'Open Email and click Compose.',
              'Search for who it’s going to — a lead, contact or account — rather than typing a bare address. This keeps every send tied to a real record, so it shows up on that record’s own timeline too.',
              'Write it, or start from a template, and send.',
            ],
          },
          {
            q: 'See who’s actually read your emails',
            steps: [
              'Open Email — the list shows every email you’ve sent, newest first.',
              'An Opened marker appears the moment the recipient’s mail client loads the message, with a count if they’ve opened it more than once.',
              'Filter by Status (queued, sent, failed) or Read (opened / not opened) to find what you’re looking for.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'Open tracking works by a tiny invisible image loaded when the email is viewed — some recipients’ mail clients block images by default, so "not opened" can sometimes mean "opened, but we couldn’t tell," not "definitely ignored."' }],
      },
      {
        id: 'campaigns',
        title: 'Campaigns',
        roles: ALL,
        scopes: { rep: 'own', manager: 'team', exec: 'view', admin: 'full' },
        blurb: 'A tracked marketing push — a webinar, a trade show, an ad push — with its own budget, spend, and the leads/opportunities it produced. Performance (ROI, cost per lead) is computed live from what those leads actually did.',
        howtos: [
          {
            q: 'Check a campaign’s return',
            steps: [
              'Open Campaigns and pick one from the list.',
              'Leads generated, conversion rate and ROI % are all computed from real linked records.',
              'An ROI of "—" means no spend has been recorded yet — it’s not the same as a 0% return.',
            ],
          },
        ],
      },
    ],
  },
  {
    group: 'Analyse',
    intro: 'The rear-view mirror and the road ahead.',
    sections: [
      {
        id: 'reports',
        title: 'Reports & Forecast',
        roles: ALL,
        scopes: { rep: 'view', manager: 'view', exec: 'view', admin: 'full' },
        blurb: 'Six focused reports — Leads, Opportunities, Activities, Revenue, Forecast, Territories — each answering one question well rather than one configurable grid trying to answer all of them.',
        howtos: [
          {
            q: 'Find the report you need',
            steps: [
              'Open Reports and use the sub-navigation to jump to Revenue, Forecast, or any of the others.',
              'Every report reflects your data scope — a rep sees their own numbers, a manager the team’s, an executive the whole org.',
            ],
          },
        ],
      },
      {
        id: 'targets',
        title: 'Targets & Quotas',
        roles: ALL,
        blurb: 'Attainment against quota, worst-off first, so the conversation starts with whoever’s furthest behind rather than whoever shouts loudest.',
        notes: [{ type: 'example', text: 'This screen currently shows an example-data badge — the live quota-tracking backend isn’t connected yet, so treat today’s numbers as illustrative, not your team’s real attainment.' }],
      },
    ],
  },
  {
    group: 'Administration',
    intro: 'The controls that shape what everyone else sees — admin-only, on purpose.',
    sections: [
      {
        id: 'admin-users',
        title: 'Users & Roles',
        roles: ['admin'],
        blurb: 'Who exists in the system, what role(s) they hold, and exactly which screen-and-action pairs those roles unlock. A person can hold several roles at once — a rep covering as manager for a region gets the union of both.',
        howtos: [
          {
            q: 'Add a new person',
            steps: ['Open Users & Roles and click New user.', 'Fill in their name, email and starting role.', 'They receive an invitation to set their own password.'],
          },
          {
            q: 'Adjust what a role can do',
            steps: [
              'Open Role permissions under Users & Roles.',
              'Toggle a screen-and-action pair on or off for that role.',
              'An explicit deny always wins, even if another role someone holds would otherwise allow it.',
            ],
          },
        ],
      },
      {
        id: 'admin-partners',
        title: 'Partners',
        badgeNew: true,
        roles: ['manager', 'admin'],
        scopes: { rep: null, manager: 'view, create, edit', exec: null, admin: 'full' },
        blurb: 'The companies that refer leads to you — resellers, referral partners, implementation and technology partners. Registering one here is what lets you invite its staff as Partner logins, and is where every partner-sourced lead traces back to.',
        howtos: [
          {
            q: 'Register a partner company',
            steps: [
              'Open Partners under Administration and click Register partner.',
              'Give it a name and, optionally, a type (Reseller, Referral, Technology, Implementation, Distributor), a tier, and a commission percentage.',
              'Save — it’s now ready to be linked to a Partner login.',
            ],
          },
          {
            q: 'Give a partner company a login',
            steps: [
              'Open Users & Roles and click New user, same as inviting anyone else.',
              'Choose the Partner role — a Partner company picker appears once you do.',
              'Pick the partner company this person belongs to and send the invitation. They’ll see nothing in the CRM except a Leads screen scoped to what they submit themselves.',
            ],
          },
          {
            q: 'Retire a partner',
            steps: [
              'Open the partner from the list and switch it to Inactive, then save.',
              'There’s no delete — a retired partner’s history (who they referred, when) stays on the record.',
            ],
          },
        ],
        notes: [{ type: 'tip', text: 'A partner-company login can only create and see the leads it submitted itself — not a shared queue with colleagues at the same partner company, and nothing else in the CRM.' }],
      },
      {
        id: 'admin-master',
        title: 'Master Data',
        roles: ['admin'],
        blurb: 'The dropdown lists every other screen reads from — lead sources, industries, opportunity stages, lost reasons, territories, and more. Everyone can read these to fill out a form; only an admin edits the list itself.',
        howtos: [
          {
            q: 'Add or reorder a picklist value',
            steps: [
              'Open Master Data and pick the reference list — e.g. Lead Sources.',
              'Add a new value, rename one, or drag to reorder how it appears in dropdowns everywhere else.',
              'Retiring a value marks it inactive rather than deleting it — records that already used it keep reading correctly.',
            ],
          },
        ],
      },
      {
        id: 'admin-integrations',
        title: 'Integrations',
        badgeNew: true,
        roles: ['admin'],
        blurb: 'Where the organisation’s outbound email provider lives — a SendGrid or SMTP connection, with the API key encrypted the moment it’s saved. Every template send and campaign email goes out through whichever provider is marked default.',
        howtos: [
          {
            q: 'Connect an email provider',
            steps: [
              'Add a provider with its from address, from name, and API key.',
              'Send a one-off test message to confirm it actually delivers before relying on it.',
              'Mark it default — that’s the connection every template send and campaign email uses from then on.',
            ],
          },
        ],
        notes: [{ type: 'example', text: 'There’s no dedicated screen for this yet in the sidebar — an administrator sets it up once directly, after which sending "just works" for everyone else.' }],
      },
      {
        id: 'admin-other',
        title: 'Automation, Audit & Security',
        roles: ['admin'],
        blurb: 'Workflow Builder (rules that act without anyone clicking), the Audit Log (who changed what, when), and Security (password policy, session and MFA rules) all live here.',
        notes: [{ type: 'example', text: 'All three currently show an example-data badge — you can see what each screen is for and how it’s laid out, but the live rule engine, audit trail and security-policy backend aren’t connected yet.' }],
      },
    ],
  },
];

/** The "who can do what" cheat sheet — same numbers as the seeded role grants. */
export const MATRIX = [
  { area: 'Leads', rep: 'Own', manager: 'Team', exec: null, admin: 'Full', partner: 'Own (submitted)' },
  { area: 'Accounts & Contacts', rep: 'Own', manager: 'Team', exec: 'View', admin: 'Full', partner: null },
  { area: 'Opportunities & Pipeline', rep: 'Own', manager: 'Team', exec: 'View', admin: 'Full', partner: null },
  { area: 'Quotations & Approvals', rep: 'Own', manager: 'Team + approve', exec: null, admin: 'Full', partner: null },
  { area: 'Activities & Follow-ups', rep: 'Own', manager: 'Team', exec: null, admin: 'Full', partner: null },
  { area: 'Email Templates', rep: 'Own', manager: 'Team', exec: null, admin: 'Full', partner: null },
  { area: 'Email (inbox)', rep: 'Own', manager: 'Team', exec: null, admin: 'Full', partner: null },
  { area: 'Campaigns', rep: 'Own', manager: 'Team', exec: 'View', admin: 'Full', partner: null },
  { area: 'Reports, Forecast, Targets', rep: 'View', manager: 'View', exec: 'View', admin: 'Full', partner: null },
  { area: 'My Team', rep: null, manager: 'Create + edit', exec: null, admin: 'Full', partner: null },
  { area: 'Partners (admin)', rep: null, manager: 'View, create, edit', exec: null, admin: 'Full', partner: null },
  { area: 'Users, Roles & System config', rep: null, manager: null, exec: null, admin: 'Full', partner: null },
];
